import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { StartupService } from '../service-ui/startup.service';
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";
import {ChargeProfile} from "../../model/ChargeProfile";
import {environment} from "../../environments/environment";

/**
 * Raw card data submitted to the merchant gateway for tokenization.
 * This object should ONLY ever travel to the dedicated tokenization endpoint;
 * none of it (especially `cardNumber` and `csc`) is ever persisted client-side.
 */
export interface CreateChargeProfileRequest {
  /** User-facing label for the saved profile (e.g., "Company Visa"). */
  name: string;
  /** Cardholder first name as printed on the card. */
  firstName: string;
  /** Cardholder last name as printed on the card. */
  lastName: string;
  /** Full PAN — 13–19 digits, no spaces. */
  cardNumber: string;
  /** Expiration month 1-12. */
  expMonth: number;
  /** Four-digit expiration year. */
  expYear: number;
  /** Card security code (CVV/CVC). */
  csc: string;
  /** Billing address line 1. */
  address1: string;
  /** Billing address line 2 (optional). */
  address2?: string;
  /** Billing city. */
  city: string;
  /** Billing state / region. */
  state: string;
  /** Billing postal code (used for AVS). */
  postalCode: string;
  /** Billing country (ISO code, e.g., 'US'). */
  country: string;
  /** True to mark this profile as the organization's default. */
  setAsDefault?: boolean;
}

@Injectable()
export class ChargeProfileService extends AbstractLiveDataService<ChargeProfile> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.notificationsService.setChargeProfileService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): ChargeProfile {
    return new ChargeProfile(args);
  }

  getLoggingObjectTypeName (): string {
    return "ChargeProfile";
  }

  supportsParallel(): boolean {
    return false;
  }

  getURLEndPoint (): string {
    return "chargeprofile";
  }

  getLinkData(): any[] {
    return [];
  }

  /**
   * Tokenize a credit card with the merchant gateway and persist the resulting
   * ChargeProfile. The raw card number and CSC travel to a dedicated endpoint
   * that hands them to the gateway and stores only the returned token plus
   * safe-to-display metadata (brand, last4, exp month/year, cardholder name).
   *
   * Unlike the inherited `addItem()`, this does NOT round-trip a full ChargeProfile
   * object — the server constructs it from the gateway response.
   *
   * @param request The raw card + billing fields needed for tokenization.
   * @returns Observable of the persisted ChargeProfile (without any secret material).
   */
  createChargeProfile(request: CreateChargeProfileRequest): Observable<ChargeProfile> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/create`;
    this.messageService.add(`ChargeProfileService: createChargeProfile name=${request.name}`);
    return this.http.post<any>(url, request, this.startupService.getHttpOptions()).pipe(
      map(response => {
        if (!response) { return undefined; }
        // The server returns the safe ChargeProfile shape (no PAN, no CSC).
        return this.addUpdateLocalItem(response as ChargeProfile);
      }),
      catchError(this.handleError(`createChargeProfile name=${request.name}`))
    );
  }

  /**
   * Mark the given ChargeProfile as the org's default. The server atomically
   * clears any previous default and returns both the new and previous
   * profiles. Matches the server endpoint
   * `POST chargeprofile/setdefault` shipped 2026-06-24 (cart redesign S-4-2).
   *
   * Use this instead of the previous PUT-the-whole-profile pattern in
   * `CartPageComponent.makeChargeProfileDefault` — that path round-trips
   * fields the server doesn't need and races on the default-toggle.
   *
   * @returns Observable of the newly-default profile. The previous default
   *          (if any) arrives via the existing `Update` notification channel
   *          and updates `this.objects` independently.
   */
  setDefault(profileId: number): Observable<ChargeProfile> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/setdefault`;
    this.messageService.add(`ChargeProfileService: setDefault id=${profileId}`);
    return this.http.post<any>(url, { chargeProfileId: profileId }, this.startupService.getHttpOptions()).pipe(
      map(response => {
        if (!response) { return undefined; }
        const r = response as { newDefault?: ChargeProfile; previousDefault?: ChargeProfile };
        if (r.previousDefault) {
          this.addUpdateLocalItem(r.previousDefault);
        }
        if (r.newDefault) {
          return this.addUpdateLocalItem(r.newDefault);
        }
        return undefined;
      }),
      catchError(this.handleError(`setDefault id=${profileId}`))
    );
  }

  /**
   * Soft-delete (state = INACTIVE) the given ChargeProfile. Matches the
   * server endpoint `DELETE chargeprofile/:id` shipped 2026-06-24 (cart
   * redesign S-4-3). The base class's `deleteItem` already targets the
   * correct URL and removes the row from `this.objects`; this wrapper is
   * a thin alias for clarity at the call site.
   */
  deleteProfile(profile: ChargeProfile): Observable<ChargeProfile> {
    this.messageService.add(`ChargeProfileService: deleteProfile id=${profile && profile.ID}`);
    return this.deleteItem(profile);
  }
}
